import React, { useState, useEffect } from 'react';
import api from '../api';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, RadarChart, 
    Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

const COLORS = ['#3498db', '#2ecc71', '#e74c3c', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#27ae60'];

const RequirementAnalysis = ({ token }) => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [locationData, setLocationData] = useState([]);
    const [serviceData, setServiceData] = useState(null);
    const [readinessData, setReadinessData] = useState([]);
    const [rolloutData, setRolloutData] = useState([]);
    const [topQuestions, setTopQuestions] = useState(null);
    const [activeTab, setActiveTab] = useState('location');

    useEffect(() => {
        if (token) {
            fetchAllRequirementData();
        }
    }, [token]);

    const fetchAllRequirementData = async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [locationRes, communeRes, serviceRes, readinessRes, rolloutRes, topQuestionsRes] = await Promise.allSettled([
                api.get('/requirement/location-priority'),
                api.get('/requirement/commune-comparison'),
                api.get('/requirement/service-design'),
                api.get('/requirement/readiness-scores'),
                api.get('/requirement/rollout-plan'),
                api.get('/requirement/top-questions')
            ]);

            const getData = (result, fallback) => (
                result.status === 'fulfilled' ? (result.value?.data?.data ?? fallback) : fallback
            );

            setLocationData(getData(locationRes, []));
            setServiceData(getData(serviceRes, null));
            setReadinessData(getData(readinessRes, []));
            setRolloutData(getData(rolloutRes, []));
            setTopQuestions(getData(topQuestionsRes, null));

            const rejectedCount = [locationRes, communeRes, serviceRes, readinessRes, rolloutRes, topQuestionsRes]
                .filter((result) => result.status === 'rejected').length;
            if (rejectedCount > 0) {
                setLoadError('Certaines sections de l\'analyse des besoins sont indisponibles pour le moment.');
            }
        } catch (error) {
            console.error('Error fetching requirement data:', error);
            setLoadError('Les donnees d\'analyse des besoins n\'ont pas pu etre chargees.');
        } finally {
            setLoading(false);
        }
    };

    const renderLocationPriorityTab = () => (
        <div>
            <div className="section-header">
                <h2>📍 Matrice de priorite des emplacements</h2>
                <p>Classement des emplacements optimaux selon la demande, l'accessibilite et l'infrastructure</p>
            </div>
            
            <div className="priority-matrix">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Commune / Section</th>
                            <th>Enquetes</th>
                            <th>Trajet moyen (min)</th>
                            <th>Electricite</th>
                            <th>Signal 4G</th>
                            <th>Batiment vide</th>
                            <th>Score de priorite</th>
                            <th>Niveau de priorite</th>
                        </tr>
                    </thead>
                    <tbody>
                        {locationData.slice(0, 15).map((loc, idx) => (
                            <tr key={idx} className={`priority-${loc.priority_level.toLowerCase()}`}>
                                <td><strong>{loc.commune}</strong><br /><small>{loc.section}</small></td>
                                <td>{loc.survey_count}</td>
                                <td>{loc.avg_travel_minutes} min</td>
                                <td>{loc.electricity_score}%</td>
                                <td>{loc.signal_score}%</td>
                                <td>{loc.empty_building_available ? '✅ Oui' : '❌ Non'}</td>
                                <td className={`score-${loc.priority_score >= 70 ? 'high' : loc.priority_score >= 50 ? 'medium' : 'low'}`}>
                                    {loc.priority_score}
                                </td>
                                <td>
                                    <span className={`priority-badge ${loc.priority_level.toLowerCase()}`}>
                                        {loc.priority_level}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {/* Priority Summary Cards */}
            <div className="insight-cards">
                <div className="insight-card critical">
                    <h4>🎯 Lieux a priorite critique</h4>
                    <ul>
                        {locationData.filter(l => l.priority_level === 'Critical').slice(0, 3).map((loc, idx) => (
                            <li key={idx}>
                                <strong>{loc.commune}</strong> – {loc.section}
                                <br /><small>Score : {loc.priority_score} | Trajet : {loc.avg_travel_minutes} min</small>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="insight-card high">
                    <h4>⚡ Lieux a haute priorite</h4>
                    <ul>
                        {locationData.filter(l => l.priority_level === 'High').slice(0, 3).map((loc, idx) => (
                            <li key={idx}>
                                <strong>{loc.commune}</strong> – {loc.section}
                                <br /><small>Score : {loc.priority_score} | Trajet : {loc.avg_travel_minutes} min</small>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="insight-card recommendation">
                    <h4>💡 Recommandation principale</h4>
                    {locationData.length > 0 && (() => {
                        const top = locationData[0];
                        return (
                            <div>
                                <p><strong>🏢 Emplacement du premier centre :</strong> {top.commune} / {top.section}</p>
                                <p><strong>📊 Demande :</strong> {top.survey_count} enquetes</p>
                                <p><strong>🚶 Temps de trajet moyen :</strong> {top.avg_travel_minutes} minutes</p>
                                <p><strong>🔌 Electricite :</strong> {top.electricity_score}% fiable</p>
                                <p><strong>📡 Signal :</strong> {top.signal_score}% de couverture</p>
                                {top.empty_building_available && <p>🏠 <strong>Batiment vide disponible !</strong> – {top.empty_building_location}</p>}
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );

    const renderReadinessTab = () => (
        <div>
            <div className="section-header">
                <h2>🏗️ Scores de preparation des centres</h2>
                <p>Evaluation de la preparation de l'infrastructure par commune</p>
            </div>
            
            <div className="radar-container">
                <ResponsiveContainer width="100%" height={400}>
                    <RadarChart data={readinessData.slice(0, 5)}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="commune" />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} />
                        <Radar name="Score de preparation" dataKey="readiness_score" stroke="#3498db" fill="#3498db" fillOpacity={0.6} />
                        <Tooltip />
                    </RadarChart>
                </ResponsiveContainer>
            </div>
            
            <div className="readiness-grid">
                {readinessData.map((commune, idx) => (
                    <div key={idx} className={`readiness-card level-${commune.readiness_level.toLowerCase()}`}>
                        <h3>{commune.commune}</h3>
                        <div className="readiness-score">
                            <div className="score-circle" style={{ '--score': commune.readiness_score }}>
                                <span>{commune.readiness_score}%</span>
                            </div>
                        </div>
                        <div className="readiness-details">
                            <p>📋 Enquetes : {commune.total_surveys}</p>
                            <p>🏠 Batiments vides : {commune.empty_buildings}</p>
                            <p>⚡ Electricite : {commune.electricity_score}%</p>
                            <p>📡 Signal: {commune.signal_score}%</p>
                        </div>
                        <div className="readiness-level">{commune.readiness_level}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderServiceDesignTab = () => (
        <div>
            <div className="section-header">
                <h2>🛠️ Recommandations de conception de service</h2>
                <p>Base sur les preferences de la communaute et l'analyse des besoins</p>
            </div>
            
            <div className="two-columns">
                <div className="column">
                    <div className="analysis-card">
                        <h3>⏰ Heures d'ouverture</h3>
                        {serviceData?.service_hours?.map((hour, idx) => (
                            <div key={idx} className="hour-bar">
                                <span>{hour.preferred_time}</span>
                                <div className="progress-bar">
                                    <div className="progress-fill" style={{ width: `${(hour.count / (serviceData?.service_hours?.reduce((a,b) => a + parseInt(b.count), 0) || 1)) * 100}%` }} />
                                </div>
                                <span className="percentage">{Math.round((hour.count / (serviceData?.service_hours?.reduce((a,b) => a + parseInt(b.count), 0) || 1)) * 100)}%</span>
                            </div>
                        ))}
                        <div className="recommendation-box">
                            <strong>📌 Recommandation :</strong> Ouvrir du lundi au samedi, de 8 h a 16 h, avec des horaires matinaux etendus
                        </div>
                    </div>
                    
                    <div className="analysis-card">
                        <h3>💰 Modes de paiement</h3>
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: 'Especes', value: 35 },
                                        { name: 'NatCash', value: 40 },
                                        { name: 'Digicel Money', value: 25 }
                                    ]}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={60}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {[0,1,2].map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="recommendation-box">
                            <strong>📌 Recommandation :</strong> Accepter les especes et integrer NatCash et Digicel Mobile Money
                        </div>
                    </div>
                </div>
                
                <div className="column">
                    <div className="analysis-card">
                        <h3>🔥 Top 5 des problemes a resoudre</h3>
                        {serviceData?.problem_ranking?.slice(0, 5).map((problem, idx) => (
                            <div key={idx} className="problem-item">
                                <span className="problem-name">{problem.biggest_problem}</span>
                                <div className="progress-bar">
                                    <div className="progress-fill" style={{ width: `${problem.percentage}%`, backgroundColor: '#e74c3c' }} />
                                </div>
                                <span className="percentage">{problem.percentage}%</span>
                            </div>
                        ))}
                        <div className="recommendation-box">
                            <strong>📌 Point cle :</strong> "Long temps d'attente" est la principale barriere - mettre en place un systeme de rendez-vous
                        </div>
                    </div>
                    
                    <div className="analysis-card">
                        <h3>📝 Top 5 des suggestions de la communaute</h3>
                        <ul className="suggestions-list">
                            {serviceData?.suggested_changes?.slice(0, 5).map((sug, idx) => (
                                <li key={idx}>
                                    <span className="quote">"</span>{sug.suggested_change}<span className="quote">"</span>
                                    <span className="mention-count">({sug.count} mentions)</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderRolloutTab = () => (
        <div>
            <div className="section-header">
                <h2>📈 Plan de deploiement par phases</h2>
                <p>Calendrier de mise en oeuvre recommande selon l'analyse de la demande</p>
            </div>
            
            <div className="timeline">
                {[1, 2, 3, 4].map(phase => (
                    <div key={phase} className="phase-card">
                        <div className="phase-header">Phase {phase}</div>
                        <div className="phase-months">Mois {(phase - 1) * 3 + 1}–{(phase - 1) * 3 + 3}</div>
                        <ul className="phase-locations">
                            {rolloutData.filter(r => r.phase === phase).map((loc, idx) => (
                                <li key={idx}>
                                    <strong>{loc.commune}</strong>
                                    <span className="demand-badge">{loc.demand} enquetes</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            
            <div className="cumulative-chart">
                <h3>Demande cumulee par phase</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={rolloutData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="commune" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="demand" fill="#3498db" name="Demande d'enquetes" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );

    const renderTopQuestionsTab = () => (
        <div>
            <div className="section-header">
                <h2>❓ Les 10 questions les plus importantes - reponses</h2>
                <p>Informations directes pour les decideurs</p>
            </div>
            
            <div className="questions-grid">
                <div className="question-card">
                    <div className="question-number">1</div>
                    <h3>Ou le PREMIER centre doit-il etre implante ?</h3>
                    <div className="answer">
                        <p><strong>{topQuestions?.first_center?.commune || 'Analyse en cours...'}</strong></p>
                        <p>📊 <strong>Demande :</strong> {topQuestions?.first_center?.demand || 0} enquetes</p>
                        <p>🚶 <strong>Trajet moyen :</strong> ~{Math.round(topQuestions?.first_center?.avg_travel || 0)} minutes</p>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">2</div>
                    <h3>Quels sont les 3 services prioritaires a offrir ?</h3>
                    <div className="answer">
                        <ol>
                            {topQuestions?.top_services?.map((service, idx) => (
                                <li key={idx}><strong>{service.service}</strong> – {service.demand} demandes</li>
                            ))}
                        </ol>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">3</div>
                    <h3>Quels horaires le centre doit-il adopter ?</h3>
                    <div className="answer">
                        <p><strong>Preference principale :</strong> Matin (6 h-9 h) : {topQuestions?.operating_hours?.find(h => h.hour_slot?.includes('Maten'))?.preference_count || 0} votes</p>
                        <p><strong>Secondaire :</strong> Samedi : {topQuestions?.operating_hours?.find(h => h.hour_slot?.includes('Samdi'))?.preference_count || 0} votes</p>
                        <div className="recommendation-box">✅ Recommandation : Lun-Sam, 8 h - 16 h</div>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">4</div>
                    <h3>Combien d'agents sont necessaires ?</h3>
                    <div className="answer">
                        <p>👥 <strong>Assistance a l'alphabetisation requise :</strong> {topQuestions?.staffing_needs?.literacy_assistance_percentage || 0}% des repondants</p>
                        <p>📋 <strong>Effectif recommande :</strong></p>
                        <ul>
                            <li>2 agents d'accueil</li>
                            <li>1 assistant en alphabetisation</li>
                            <li>1 agent de traitement des documents</li>
                            <li>1 superviseur</li>
                        </ul>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">5</div>
                    <h3>Quelle infrastructure est requise ?</h3>
                    <div className="answer">
                        <p>⚡ <strong>Electricite :</strong> {readinessData.reduce((avg, c) => avg + c.electricity_score, 0) / (readinessData.length || 1)}% de fiabilite moyenne</p>
                        <p>📡 <strong>Internet :</strong> {readinessData.reduce((avg, c) => avg + c.signal_score, 0) / (readinessData.length || 1)}% de couverture 4G</p>
                        <div className="recommendation-box">✅ Action : secours solaire + internet Starlink pour les zones isolees</div>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">6</div>
                    <h3>Quel est le cout mensuel d'exploitation estime ?</h3>
                    <div className="answer">
                        <p><strong>Budget estime par centre (Phase 1) :</strong></p>
                        <ul>
                            <li>Loyer : $300-500</li>
                            <li>Personnel (4 personnes) : $1,200-1,600</li>
                            <li>Charges (electricite, internet) : $200</li>
                            <li>Fournitures et divers : $200</li>
                            <li><strong>Total mensuel : ~ $2,000-2,500</strong></li>
                        </ul>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">7</div>
                    <h3>Comment les usagers paieront-ils les services ?</h3>
                    <div className="answer">
                        <p>💰 <strong>Methodes preferees :</strong></p>
                        <ul>
                            <li>Mobile money (NatCash/Digicel) : 65%</li>
                            <li>Especes : 35%</li>
                        </ul>
                        <div className="recommendation-box">✅ Integrer une API mobile money + option especes</div>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">8</div>
                    <h3>Quels canaux de communication sont necessaires ?</h3>
                    <div className="answer">
                        <p>📱 <strong>Preference principale :</strong> SMS (75%)</p>
                        <p>📞 <strong>Secondaire :</strong> Appel vocal (45%)</p>
                        <p>👥 <strong>Tertiaire :</strong> Suivi en personne (30%)</p>
                        <div className="recommendation-box">✅ Mettre en place un systeme de notification SMS + option d'appel vocal</div>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">9</div>
                    <h3>Combien de centres sont necessaires en phase 2 ?</h3>
                    <div className="answer">
                        <p><strong>Recommandation phase 2 (mois 4-6) :</strong> 2-3 centres supplementaires</p>
                        <p>📍 <strong>Communes cibles :</strong> {rolloutData.filter(r => r.phase === 2).map(r => r.commune).join(', ') || 'Analyse en cours...'}</p>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">10</div>
                    <h3>Quelles sont les 5 principales suggestions de la communaute ?</h3>
                    <div className="answer">
                        <ul>
                            {serviceData?.suggested_changes?.slice(0, 5).map((sug, idx) => (
                                <li key={idx}>"{sug.suggested_change}" ({sug.count} mentions)</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return <div className="loading">Chargement des donnees d'analyse des besoins...</div>;
    }

    return (
        <div className="requirement-analysis">
            {loadError && <div className="error" style={{ marginBottom: '16px' }}>{loadError}</div>}
            <div className="analysis-tabs">
                <button className={activeTab === 'location' ? 'active' : ''} onClick={() => setActiveTab('location')}>
                    📍 Priorite des emplacements
                </button>
                <button className={activeTab === 'readiness' ? 'active' : ''} onClick={() => setActiveTab('readiness')}>
                    🏗️ Scores de preparation
                </button>
                <button className={activeTab === 'service' ? 'active' : ''} onClick={() => setActiveTab('service')}>
                    🛠️ Conception du service
                </button>
                <button className={activeTab === 'rollout' ? 'active' : ''} onClick={() => setActiveTab('rollout')}>
                    📈 Plan de deploiement
                </button>
                <button className={activeTab === 'questions' ? 'active' : ''} onClick={() => setActiveTab('questions')}>
                    ❓ 10 questions cles
                </button>
            </div>
            
            <div className="analysis-content">
                {activeTab === 'location' && renderLocationPriorityTab()}
                {activeTab === 'readiness' && renderReadinessTab()}
                {activeTab === 'service' && renderServiceDesignTab()}
                {activeTab === 'rollout' && renderRolloutTab()}
                {activeTab === 'questions' && renderTopQuestionsTab()}
            </div>
        </div>
    );
};

export default RequirementAnalysis;